import bhiveIcon from 'Assets/images/bhive-logo-short.svg';
import smsIcon from 'Assets/images/directory-chat.svg';
import makeCallIcon from 'Assets/images/phone-directory.svg';
import LazyLoader from 'Components/LazyLoader';
import PhoneNumbersPopup from 'Components/PhoneNumbersPopup';
import { Heading } from 'Components/shared/Heading';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { CONTACTS_PER_PAGE } from 'Constants/contacts';
import { debounce, uniqBy } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';

import { ContactModelV2 } from 'Models/ContactModelV2';
import { useCallRouter } from 'Modules/softphone/hooks';
import * as React from 'react';
import { useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate } from 'react-router';
import {
  Button,
  Dropdown,
  DropdownItemProps,
  DropdownProps,
  Grid,
  Input,
  Loader,
  Popup,
} from 'semantic-ui-react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { ContactsLinks } from './ContactsLinks';

const permanentProviders: DropdownItemProps[] = [
  { key: 'displayAll', text: 'Display all', value: null },
  {
    key: 'bHivePersonal',
    image: { avatar: true, src: bhiveIcon },
    text: 'Personal contacts',
    value: 'personal',
  },
  {
    key: 'bHiveOrganization',
    image: { avatar: true, src: bhiveIcon },
    text: 'Organization contacts',
    value: 'shared',
  },
];

export default observer(() => {
  const { person, conversation } =
    React.useContext<RootStoreProps>(MobXProviderContext);
  const navigate = useNavigate();

  const { callWithPerson } = useCallRouter();

  const [selectedContact, setSelectedContact] = useState<ContactModelV2>(null);
  const [operationType, setOperationType] = useState<'call' | 'message'>(null);
  const [dropdownOption, setDropdownOption] =
    useState<DropdownItemProps[]>(null);
  const [hasImageError, setHasImageError] = useState(false);

  const mapOptionsToFilter = () => {
    const externalProviders: DropdownItemProps[] = person.allSources
      ?.map((item) => {
        return {
          key: item.id,
          image: {
            avatar: true,
            src: item.providerImageSmall,
            style: item.sourceData.type === 'microsoft' ? { maxWidth: 20 } : {},
          },
          text: item.email,
          value: item.id,
        };
      })
      .filter(Boolean);

    const mergedProvides = [...permanentProviders, ...externalProviders];
    if (mergedProvides.length > dropdownOption?.length || !dropdownOption) {
      setDropdownOption(mergedProvides);
    }
  };

  const filterList = debounce((q: string) => {
    const {
      contactPageNumber,
      contactSearchTerm,
      contactFilterValue,
      setContactPageNumber,
      getSearchListContacts,
    } = person;

    let pageNumber = contactPageNumber;
    if (
      (contactSearchTerm || contactFilterValue?.value) &&
      contactPageNumber > 1
    ) {
      setContactPageNumber(1);
      //dont remove pageNumber, because for what ever reason contactPageNumber is not updated, from source pick
      pageNumber = 1;
    }
    void getSearchListContacts(
      CONTACTS_PER_PAGE,
      pageNumber,
      contactSearchTerm,
      undefined,
      contactFilterValue?.value?.toString()
    );
  }, 500);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    e.preventDefault();
    person.setContactSearchTerm(value);
    filterList(value);
  };

  const makeCall =
    (contact: ContactModelV2) => (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (contact.phoneNumbers.length > 1) {
        setSelectedContact(contact);
        setOperationType('call');
      } else if (contact?.phoneNumbers.length === 1) {
        void callWithPerson(undefined, contact.phoneNumbers[0].number);
      }
    };

  const handleOpenChat =
    (contact: ContactModelV2) =>
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (contact?.phoneNumbers.length > 1) {
        setSelectedContact(contact);
        setOperationType('message');
      } else if (contact?.phoneNumbers.length === 1) {
        const phone = contact?.phoneNumbers[0].number;
        const resp = await conversation.loadOrCreateConversationWithPost(
          null,
          phone
        );
        navigate(`/chat/conversations/${resp.data.id}/menu`);
      }
    };

  const handleLoadMoreContacts = () => {
    const {
      loadingInfinite,
      setContactPageNumber,
      getSearchListContacts,
      contactPageNumber,
      contactFilterValue,
      contactSearchTerm,
    } = person;

    if (!loadingInfinite) {
      const currentPage = contactPageNumber + 1;
      setContactPageNumber(currentPage);
      void getSearchListContacts(
        CONTACTS_PER_PAGE,
        currentPage,
        contactSearchTerm,
        true,
        contactFilterValue?.value?.toString()
      );
    }
  };

  const handleSourcePick = (e, d: DropdownProps) => {
    const pickedSource = dropdownOption.find(
      (item) => item.provider === d.value || item.value === d.value
    );

    person.setContactPageNumber(1);

    if (pickedSource) {
      person.setContactFilterValue(pickedSource.value ? pickedSource : null);
      filterList(person.contactSearchTerm);
    }
  };

  const clearSelected = () => {
    setSelectedContact(null);
  };

  const openEditMode = (contact: ContactModelV2) => (e: Event) => {
    e.stopPropagation();
    person.setEditContact(contact);
    navigate('/addressBook/contact', { state: { from: '/contacts' } }); //
  };

  const getSourceProvider = (accountId: string) => {
    const provider = person.allSources.find(
      (source) => source.id.toString() === accountId
    )?.sourceData.type;
    return person.changeProviderName(provider || '');
  };

  const displayProvider = (contact: ContactModelV2) => {
    return contact.sourceData.accountId
      ? getSourceProvider(contact.sourceData.accountId)
      : 'B-Hive';
  };

  const mapOrgData = (contact: ContactModelV2) => {
    if (contact.organization && contact.role) {
      return `${contact.organization}, ${contact.role}`;
    } else if (contact.organization) {
      return contact.organization;
    } else if (contact.role) {
      return contact.role;
    }
    return '';
  };

  useEffect(() => {
    mapOptionsToFilter();
  });

  useEffect(() => {
    const INITIAL_PAGE_NUMBER = 1;
    person.setContactPageNumber(INITIAL_PAGE_NUMBER);

    void person.getSearchListContacts(
      CONTACTS_PER_PAGE,
      INITIAL_PAGE_NUMBER,
      person.contactSearchTerm,
      undefined,
      person.contactFilterValue?.value?.toString()
    );
  }, [
    person,
    person.contactFilterValue,
    person.contactSearchTerm,
    person.getSearchListContacts,
  ]);

  const filteredSourceOfData = person.allContacts.filter((contact) => contact);
  const currentDataLength = person.contactPageNumber * 20;

  return (
    <div className="all-contacts" id="contacts" onClick={clearSelected}>
      <Heading variant="h2">Contacts</Heading>
      <div className="semantic-flex-row top-15">
        <Input
          autoFocus={true}
          className="search-field"
          value={person.contactSearchTerm || ''}
          placeholder="Search"
          onChange={handleSearch}
        />
        <div className="filter-source">
          <div className="filter-text">Filter by source</div>
          <Dropdown
            data-private
            placeholder="Display all"
            fluid
            selection
            options={uniqBy(dropdownOption, 'key')}
            onChange={handleSourcePick}
            value={person.contactFilterValue?.value}
          />
        </div>
      </div>
      {person.loadingContacts ? (
        <LazyLoader className="addressbook-loader" />
      ) : (
        <InfiniteScroll
          initialScrollY={1}
          dataLength={currentDataLength} //This is important field to render the next data
          next={handleLoadMoreContacts}
          hasMore={!person.loadingInfinite && person.hasMoreContacts}
          loader={<></>}
          endMessage={<></>}
          pullDownToRefreshThreshold={4}
          scrollableTarget="contactList"
        >
          <Grid columns="four" id="contactList" className="list">
            {filteredSourceOfData?.length > 0 ? (
              filteredSourceOfData.map((contact) => {
                const organizationData = mapOrgData(contact);
                return (
                  <Grid.Row
                    key={contact.id}
                    className="directory-items"
                    onClick={openEditMode(contact)}
                  >
                    <Grid.Column width="7">
                      <div className="semantic-flex-row">
                        <div className="semantic-flex-column">
                          <div className="semantic-flex-row semantic-flex-align-items-center">
                            {contact.avatarUrl && !hasImageError ? (
                              <img
                                className="contact-prof-img"
                                src={contact.avatarUrl}
                                onError={() => setHasImageError(true)}
                              />
                            ) : (
                              <UserAvatar
                                name={
                                  contact.name?.replace(/\s+/, '') === ''
                                    ? null
                                    : contact.name
                                }
                                conversationGrouping="OneOnOne"
                              />
                            )}
                            <div className="directory-user-info">
                              <div data-private>
                                {contact.name}
                                {/* show this only if DisplayAll selected */}
                                {!person.contactFilterValue?.value && (
                                  <span className="provider">
                                    ({displayProvider(contact)})
                                  </span>
                                )}
                              </div>
                              <div>{organizationData}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Grid.Column>
                    <Grid.Column width="6">
                      <ContactsLinks {...{ contact, callWithPerson }} />
                    </Grid.Column>
                    <Grid.Column width="3" textAlign="right">
                      <div>
                        {contact.phoneNumbers.length > 0 && (
                          <div className="semantic-flex-row semantic-flex-align-items-center semantic-flex-justify-end position-relative">
                            {selectedContact &&
                              selectedContact?.id === contact.id && (
                                <PhoneNumbersPopup
                                  phoneNumbers={contact.phoneNumbers}
                                  callWithPerson={callWithPerson}
                                  loadOrCreateConversationWithPost={
                                    conversation.loadOrCreateConversationWithPost
                                  }
                                  operationType={operationType}
                                />
                              )}
                            <Popup
                              key="start-chat"
                              inverted
                              position="bottom center"
                              content={'Start messaging'}
                              trigger={
                                <div>
                                  <Button
                                    onClick={handleOpenChat(contact)}
                                    size="small"
                                    compact
                                    basic
                                    className="semantic-flex-row semantic-flex-align-items-center"
                                  >
                                    <img
                                      className="icon-18px chat-icon"
                                      src={smsIcon}
                                    />
                                  </Button>
                                </div>
                              }
                            />

                            <Popup
                              key="start-call"
                              inverted
                              content={'Start Call'}
                              position="bottom center"
                              trigger={
                                <div className={'call-connecting'}>
                                  <Button
                                    onClick={makeCall(contact)}
                                    size="small"
                                    compact
                                    id="call-person"
                                    basic
                                    className="semantic-flex-row align-items-center"
                                  >
                                    {
                                      <img
                                        id="phone-call"
                                        className="icon-18px"
                                        src={makeCallIcon}
                                      />
                                    }
                                  </Button>
                                </div>
                              }
                            />
                          </div>
                        )}
                      </div>
                    </Grid.Column>
                  </Grid.Row>
                );
              })
            ) : (
              <div className="no-contacts">No contacts found</div>
            )}
          </Grid>
          {person.loadingInfinite && (
            <Loader className="loading-more-contacts" />
          )}
        </InfiniteScroll>
      )}
    </div>
  );
});
